import { Row, RowSelectionState, Updater } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo } from 'react';
import {
  MultiSelectScope,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { packNumericIds } from 'src/contexts/MultiSelectionContext/MultiSelectionContext.utils';
import { PurchaseOrderTableProps } from 'src/tables/PurchaseTable/PurchaseTable.types';
import {
  PurchaseOrderTicketGroup,
  PurchaseViewMode,
} from 'src/WebApiController';

const getRowIdForPurchase = (
  originalRow: PurchaseOrderTicketGroup | null,
  index: number
) =>
  // For TG sub-rows, use packed format [poId, tgId] to support TG-level selection in time view
  originalRow?.isTktGrp && originalRow.tgId
    ? packNumericIds([originalRow.id, originalRow.tgId])
    : (originalRow?.id ?? index).toString();

const getRowIdForTicketGroup = (
  originalRow: PurchaseOrderTicketGroup | null,
  index: number
) =>
  originalRow?.tgId
    ? packNumericIds([originalRow.id, originalRow.tgId])
    : index.toString();

export function usePurchaseTableSelection({
  data,
  groupId,
  viewMode,
  disableSelection,
  onItemSelectionsChanged,
  setVisibleRowIds,
}: Pick<
  PurchaseOrderTableProps,
  'groupId' | 'viewMode' | 'disableSelection' | 'onItemSelectionsChanged'
> & {
  data: (PurchaseOrderTicketGroup | null)[] | undefined;
  setVisibleRowIds: (visibleRowIds: string[] | undefined) => void;
}) {
  const {
    selectionMode,
    setGroupItems,
    getGroupToggleState,
    getSelection,
    totalItemsSelected,
    updateGroupFilteredIds,
  } = useMultiSelectionContext();
  const rowSelection = useMemo(() => {
    // Don't call "getSelection" outside hook, the result is not memoized
    // and it expensive to calculate.
    const groupSelection = getSelection(groupId);
    const result: Record<number | string, boolean> = {};

    const isGroupSelectedWithoutClientSideFilter =
      getGroupToggleState(groupId).isGroupSelected &&
      !groupSelection.items.itemIds.length;

    // Helper to check if a row ID is selected
    const isRowSelected = (rowId: string) =>
      isGroupSelectedWithoutClientSideFilter ||
      (groupSelection?.items.itemIds ?? []).includes(rowId);

    // Type casting needed because of a bug in older TS versions: https://github.com/microsoft/TypeScript/issues/44373
    (data as (PurchaseOrderTicketGroup | null)[]).forEach((rowItem) => {
      if (viewMode === PurchaseViewMode.EventTileView) {
        if (rowItem?.tgId) {
          const rowId = getRowIdForTicketGroup(rowItem, -1);
          result[rowId] = isRowSelected(rowId);
        }
      } else {
        // For non-EventTileView (TileView), handle both PO and TG selections
        // PO rows use simple IDs
        const rowId = getRowIdForPurchase(rowItem, -1);
        if (rowId !== '-1') {
          result[rowId] = isRowSelected(rowId);
        }

        // Also include TG sub-rows in the selection state
        // TG sub-rows use packed IDs [poId, tgId]
        if (rowItem?.ticketGroupItems && rowItem.ticketGroupItems.length > 1) {
          rowItem.ticketGroupItems.forEach((tgItem) => {
            if (tgItem.tgId) {
              const tgRowId = packNumericIds([rowItem.id, tgItem.tgId]);
              result[tgRowId] = isRowSelected(tgRowId);
            }
          });
        }
      }
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalItemsSelected, // Keep it here to trigger the hook
    data,
    viewMode,
    getGroupToggleState,
    getSelection,
    groupId,
  ]);

  // Clear selections when selection mode changes
  useEffect(() => {
    onItemSelectionsChanged?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode?.mode]);

  const onRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      if (disableSelection) return;
      const newSelections =
        typeof updater === 'function' ? updater(rowSelection) : updater;

      // When a PO is selected but some of its TG sub-rows have been unchecked,
      // deselect the PO parent so the selection is no longer classified as "pure PO".
      // This prevents PO-level cost adjustments from running on a partial set of TGs.
      if (viewMode !== PurchaseViewMode.EventTileView && data) {
        for (const rowItem of data) {
          if (!rowItem || rowItem.isTktGrp) {
            continue;
          }
          const poRowId = rowItem.id.toString();
          if (
            newSelections[poRowId] &&
            rowItem.ticketGroupItems &&
            rowItem.ticketGroupItems.length > 1
          ) {
            const allTgsSelected = rowItem.ticketGroupItems.every((tgItem) => {
              if (!tgItem.tgId) {
                return true;
              }
              const tgRowId = packNumericIds([rowItem.id, tgItem.tgId]);
              return newSelections[tgRowId] === true;
            });
            if (!allTgsSelected) {
              newSelections[poRowId] = false;
            }
          }
        }
      }

      const newSelectedIds = Object.keys(newSelections).filter(
        (r) => newSelections[r] === true
      );

      setGroupItems(groupId, newSelections);
      onItemSelectionsChanged?.(
        newSelectedIds
          .filter((id) => newSelections[id])
          .map((id) => parseInt(id))
      );
    },
    [
      data,
      disableSelection,
      groupId,
      onItemSelectionsChanged,
      rowSelection,
      setGroupItems,
      viewMode,
    ]
  );

  const getRowId =
    viewMode === PurchaseViewMode.EventTileView
      ? getRowIdForTicketGroup
      : getRowIdForPurchase;

  const onFilteredRowsChange = useCallback(
    (filteredRows: Row<PurchaseOrderTicketGroup | null>[] | undefined) => {
      const filteredItemIds = filteredRows
        ?.map((r) => getRowId(r.original, r.index))
        .filter((i) => i != null) as string[] | undefined;
      updateGroupFilteredIds(groupId, filteredItemIds);
      setVisibleRowIds(filteredItemIds);
    },
    // !!!IMPORTANT: don't include setVisibleRowIds in the dependency array
    [getRowId, groupId, updateGroupFilteredIds]
  );

  return {
    getRowId,
    onRowSelectionChange,
    onFilteredRowsChange,
    rowSelection,
    showCheckbox:
      selectionMode?.mode === MultiSelectScope.AllGroups ||
      selectionMode?.mode === MultiSelectScope.SingleGroup,
  };
}
